import React, { useState, useEffect, useCallback } from "react";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  updateProfile,
  getAdditionalUserInfo,
  sendEmailVerification,
} from "firebase/auth";
import type { UserCredential, AuthError } from "firebase/auth";

import { auth, googleProvider, appleProvider } from "./firebase";
import { AuthContext } from "./context";
import { convertFirebaseUser, getErrorMessage } from "./utils";
import type { User, AuthContextType, AuthProviderProps } from "./types";
//import { BASE_URL } from "./types";
import ApiCall from "./ApiCall";
import { refreshProfileCache } from "./UserProfile";
import { logoutCurrentDevice } from "../apiCalls/Devices";

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [profileReady, setProfileReady] = useState<boolean>(false);

  useEffect(() => {
    // Listen for authentication state changes
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        const appUser = convertFirebaseUser(firebaseUser);
        setUser(appUser);
        setLoading(false);

        // Skip profile fetch for brand-new email users: their Django profile
        // doesn't exist yet — it gets created moments later by the registration
        // flow. Fetching now would produce a 404. We skip and let the
        // registration flow populate localStorage via cacheProfileSetting().
        const createdAt = new Date(
          firebaseUser.metadata.creationTime || 0,
        ).getTime();
        const isVeryNew = Date.now() - createdAt < 120_000;
        const isUnverifiedEmail =
          !firebaseUser.emailVerified &&
          firebaseUser.providerData.some((p) => p.providerId === "password");

        if (isVeryNew && isUnverifiedEmail) {
          setProfileReady(true);
        } else {
          refreshProfileCache().finally(() => setProfileReady(true));
        }
      } else {
        setUser(null);
        setLoading(false);
        setProfileReady(true);
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<User> => {
    try {
      const result: UserCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const appUser = convertFirebaseUser(result.user);

      // Get additional user info to check if it's a new user
      const additionalUserInfo = getAdditionalUserInfo(result);
      appUser.isNewUser = additionalUserInfo?.isNewUser || false;

      return appUser;
    } catch (error) {
      const authError = error as AuthError;
      throw new Error(getErrorMessage(authError.code));
    }
  };

  const loginWithGoogle = async (): Promise<User> => {
    try {
      const result: UserCredential = await signInWithPopup(
        auth,
        googleProvider,
      );
      const appUser = convertFirebaseUser(result.user);

      // Get additional user info to check if it's a new user
      const additionalUserInfo = getAdditionalUserInfo(result);
      appUser.isNewUser = additionalUserInfo?.isNewUser || false;

      return appUser;
    } catch (error) {
      const authError = error as AuthError;
      console.error("=== GOOGLE LOGIN ERROR ===");
      console.error("Error Code:", authError.code);
      console.error("Error Message:", authError.message);
      console.error("Full Error:", error);
      console.error("=========================");
      throw new Error(getErrorMessage(authError.code));
    }
  };

  const loginWithApple = async (): Promise<User> => {
    try {
      const result: UserCredential = await signInWithPopup(auth, appleProvider);
      const appUser = convertFirebaseUser(result.user);

      // Get additional user info to check if it's a new user
      const additionalUserInfo = getAdditionalUserInfo(result);
      appUser.isNewUser = additionalUserInfo?.isNewUser || false;

      return appUser;
    } catch (error) {
      const authError = error as AuthError;
      console.error("=== APPLE LOGIN ERROR ===");
      console.error("Error Code:", authError.code);
      console.error("Error Message:", authError.message);
      console.error("Full Error:", error);
      console.error("========================");
      throw new Error(getErrorMessage(authError.code));
    }
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
  ): Promise<User> => {
    try {
      const result: UserCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );

      // Update the user's display name with their first name
      await updateProfile(result.user, {
        displayName: firstName,
      });

      const appUser = convertFirebaseUser(result.user);
      // Override the name with the first name we just set
      appUser.name = firstName;

      // createUserWithEmailAndPassword only succeeds for new users, so this is
      // always true. Don't rely on getAdditionalUserInfo — it can return null.
      appUser.isNewUser = true;

      return appUser;
    } catch (error) {
      const authError = error as AuthError;
      throw new Error(getErrorMessage(authError.code));
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Free this device's slot while the ID token is still valid — must
      // happen before signOut(), which invalidates it.
      await logoutCurrentDevice();
      await signOut(auth);
      // Clear all localStorage values on logout
      localStorage.clear();
    } catch (error) {
      console.error("Logout error:", error);
      throw new Error("Failed to sign out");
    }
  };

  const resendVerificationEmail = async (): Promise<void> => {
    try {
      if (!auth.currentUser) {
        throw new Error("No user is currently signed in");
      }

      // Call backend API to send verification email
      const response = await ApiCall.makeAuthenticatedRequest(
        "/accounts/api/email/send-verification/",
        { method: "POST" },
      );

      if (response.error) {
        // Backend profile not yet created — fall back to Firebase directly
        await sendEmailVerification(auth.currentUser);
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes("too-many-requests")) {
          throw new Error(
            "Too many requests. Please wait a few minutes before trying again.",
          );
        }
        throw error;
      }
      throw new Error("Failed to send verification email");
    }
  };

  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      if (auth.currentUser) {
        // Force Firebase to re-check verification status from its servers
        await auth.currentUser.reload();

        const response = await ApiCall.makeAuthenticatedRequest(
          "/accounts/api/email/verification-status/?refresh=true",
          { method: "GET" },
        );

        const appUser = convertFirebaseUser(auth.currentUser);

        if (!response.error) {
          // If either Firebase or the backend confirms verified, treat as verified
          appUser.emailVerified =
            (response.email_verified as boolean) || appUser.emailVerified;
        }
        // If backend errored (e.g. profile not yet created), emailVerified
        // comes from auth.currentUser.reload() above — still accurate.

        setUser(appUser);
      }
    } catch (error) {
      console.error("Error refreshing user:", error);
    }
  }, []);

  const value: AuthContextType = {
    user,
    login,
    loginWithGoogle,
    loginWithApple,
    register,
    logout,
    resendVerificationEmail,
    refreshUser,
    loading,
    profileReady,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
